/**
 * Ответ на запрос группы «Подбор» — `GET /api/podbor/{хэш}`, **без входа**: похожие модели и их
 * свободные серии по салонам сейчас. Спека `docs/specs/2026-10-28-podbor-photo-design.md`.
 */
import { apiFetch } from "@/lib/api";
import type { CatalogGroup } from "@/lib/clientChats";

export type PodborSerial = {
  serial: number;
  /** Размер серии; «БР» — категория без размера, подходит любому. */
  size: string;
  location_id: number;
  city: string;
  place: string;
  transit: boolean;
};

export type PodborModel = {
  model_id: number;
  name: string;
  /** self — модель серии, same/group — то же платье в CRM, similar — связь «Похожие», photo — по фото. */
  link: string;
  score: number | null;
  photo: string | null;
  price: number | null;
  serials: PodborSerial[];
};

export type PodborAnswer = {
  token: string;
  date: string;
  note: string;
  size: string;
  location_id: number | null;
  has_photo: boolean;
  /** `glue_total` — сколько всего других моделей в склейке (`modelSimilarGroupID`), со свободными и без. */
  serial: { serial: number; location_id: number | null; glue_total: number } | null;
  same: PodborModel[];
  similar: PodborModel[];
  locations: { id: number; short: string }[];
};

export const BR = "БР";

const base = (token: string) => `/api/podbor/${encodeURIComponent(token)}`;

export function loadPodbor(token: string): Promise<PodborAnswer> {
  return apiFetch<PodborAnswer>(base(token));
}

/** Свойства фильтра «Резерва» — те же группы, что у каталога «Чатов». */
export function loadPodborFilters(token: string): Promise<{ groups: CatalogGroup[] }> {
  return apiFetch<{ groups: CatalogGroup[] }>(`${base(token)}/filters`);
}

/** Свободные серии моделей страницы под выбранные свойства. */
export function matchPodbor(token: string, models: number[], props: string[]): Promise<{ serials: number[] }> {
  const q = new URLSearchParams({ models: models.join(","), props: props.join(",") });
  return apiFetch<{ serials: number[] }>(`${base(token)}/match?${q}`);
}

/** Новый поиск — с главной `/podbor` или со страницы ответа: фото (base64 JPEG) и/или серия → хэш. */
export function searchPodbor(body: {
  photo: string;
  serial: number | null;
  size: string;
  location_id: number | null;
}): Promise<{ token: string; url: string }> {
  return apiFetch<{ token: string; url: string }>("/api/podbor/search", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Недавние поиски этого телефона — на главной `/podbor`. Только удобство: хранилище может быть пустым. */
export type PodborRecent = { token: string; serial: number | null; photo: boolean; at: string };
const RECENT_KEY = "prokat.podbor.recent";

export function recentPodbor(): PodborRecent[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(list) ? (list as PodborRecent[]) : [];
  } catch {
    return [];
  }
}

export function rememberPodbor(item: PodborRecent): void {
  try {
    const list = [item, ...recentPodbor().filter((r) => r.token !== item.token)].slice(0, 10);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // приватный режим или запрет хранилища — просто без истории
  }
}

/** Фото с телефона → JPEG до 1280 px строкой base64: сервер берёт до 900 КБ. */
export async function photoBase64(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const k = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * k);
  canvas.height = Math.round(bitmap.height * k);
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

/** Серия проходит фильтр: город (null — все), размер ("" — все; БР подходит любому) и свойства
 * «Все фильтры» (`match` — серии под них; null — свойства не выбраны). */
export function fits(
  s: PodborSerial,
  city: number | null,
  size: string,
  match: Set<number> | null = null,
): boolean {
  return (
    (city === null || s.location_id === city) &&
    (!size || s.size === size || s.size === BR) &&
    (match === null || match.has(s.serial))
  );
}
